import { useEffect, useState } from 'react';

/**
 * Chart color SSOT (T-349). The categorical palette is defined once as CSS
 * custom properties (`--chart-1`..`--chart-8`, plus `--chart-grid`/`--chart-axis`)
 * in `index.css` for light and dark, and exposed to Tailwind as `chart-1`..`chart-8`.
 *
 * Recharts sets `fill`/`stroke` as SVG presentation ATTRIBUTES, where CSS `var()`
 * does not resolve — so charts cannot use `"hsl(var(--chart-1))"` directly. Use
 * `useChartColors()` to get concrete, theme-correct `hsl(...)` strings that
 * re-resolve whenever the light/dark theme changes.
 *
 * Rules (see docs/standards/color-token-map.md): assign slots in fixed order,
 * never cycle; a 9th series folds to "Other"/facet. Never put chart series colors
 * on text — labels/values/legends use ink tokens (foreground/muted-foreground).
 * Status encodings (good/bad/warn) use the semantic tokens, not these slots.
 */

export const CHART_SLOT_VARS = [
  '--chart-1',
  '--chart-2',
  '--chart-3',
  '--chart-4',
  '--chart-5',
  '--chart-6',
  '--chart-7',
  '--chart-8',
] as const;

/** Semantic status colors for marks that encode state (loss/overdue/good), not category. */
export interface ChartStatusColors {
  success: string;
  warning: string;
  destructive: string;
  info: string;
  primary: string;
  muted: string;
}

export interface ChartColors {
  /** Categorical series colors, fixed order — concrete `hsl(...)` strings. */
  series: string[];
  /** Series color by index, folding anything past slot 8 back to the last slot. */
  seriesAt: (index: number) => string;
  /** Recessive gridline color. */
  grid: string;
  /** Axis text/tick color. */
  axis: string;
  /**
   * Semantic status colors (resolved `hsl(...)`), for marks that encode state
   * rather than category — e.g. a bar meaning loss/overdue. See color-token-map §5.
   */
  status: ChartStatusColors;
}

function readVar(name: string): string {
  if (typeof window === 'undefined') return '';
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return raw ? `hsl(${raw})` : '';
}

function resolve(): ChartColors {
  const series = CHART_SLOT_VARS.map(readVar).filter(Boolean);
  return {
    series,
    seriesAt: (index: number) =>
      series.length ? series[Math.min(index, series.length - 1)] : 'currentColor',
    grid: readVar('--chart-grid'),
    axis: readVar('--chart-axis'),
    // Status marks use the AA-safe `-text` variants, not the base tokens: they
    // step brighter in dark / darker in light, so a status bar/segment keeps ≥3:1
    // against the chart surface in BOTH themes (the base --destructive is a muted
    // 0 63% 31% in dark that reads too dark on a dark surface). See T-350.
    status: {
      success: readVar('--success-text'),
      warning: readVar('--warning-text'),
      destructive: readVar('--destructive-text'),
      info: readVar('--info-text'),
      primary: readVar('--primary-text'),
      muted: readVar('--muted-foreground'),
    },
  };
}

/**
 * Theme-aware chart colors. Re-resolves the CSS custom properties whenever the
 * theme toggles (observes `class`/`data-theme` on <html>) so Recharts marks track
 * light/dark without a hard reload.
 */
export function useChartColors(): ChartColors {
  const [colors, setColors] = useState<ChartColors>(() => resolve());

  useEffect(() => {
    const update = () => setColors(resolve());
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-theme'],
    });
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    media.addEventListener('change', update);
    return () => {
      observer.disconnect();
      media.removeEventListener('change', update);
    };
  }, []);

  return colors;
}
